import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../api/axios';
import useLocale from '../hooks/useLocale';
import './ThesisRatesEditor.css';
import useUpdateBreadcrumbs from '../hooks/useUpdateBreadcrumbs';

const ThesisRatesEditor = () => {
  // was conference_id, section_id
  const { festschrift_id, festschrift_section_id } = useParams();
  const navigate = useNavigate();
  const { getCurrentLanguage, getLocalizedName } = useLocale();
  const lang = getCurrentLanguage();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const confName = searchParams.get('conferenceName') || '';
  const sectionName = searchParams.get('sectionName') || '';

  // Обновляем хлебные крошки
  const resetBreadcrumbs = useCallback(
    (search) => {
      const sp = new URLSearchParams(search);
      const conf = sp.get('conferenceName') || '';
      const sect = sp.get('sectionName') || '';
      return [
        {
          label: conf,
          url: `/management/publishing/${festschrift_id}?conferenceName=${conf}`,
        },
        {
          label: sect,
        },
      ];
    },
    [festschrift_id],
  );
  useUpdateBreadcrumbs(resetBreadcrumbs);

  // Список «тезисов» (на самом деле статей) с оценками
  const [theses, setTheses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Локальные значения рейтингов, по ключу thesis_id (логически article_id)
  const [localRates, setLocalRates] = useState({});
  const [initialRates, setInitialRates] = useState({});
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  // Сортировка по названию
  const [sortOrder, setSortOrder] = useState('none'); // "none" | "asc" | "desc"

  // Модалка несохранённых изменений
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Загрузка с бэка: теперь /rates/festschrift_sections/{festschrift_section_id}/articles
  useEffect(() => {
    const fetchTheses = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/rates/section/${festschrift_section_id}/articles`);
        // по-прежнему ожидаем ThesisElectionDTO: thesis_id, thesis_title, rate
        setTheses(response.data);
        const initial = {};
        response.data.forEach((item) => {
          initial[item.article_id] = item.rate !== null && item.rate !== undefined ? item.rate : '';
        });
        setLocalRates(initial);
        setInitialRates(initial);
        setError('');
      } catch (err) {
        console.error(err);
        setError(lang === 'ru' ? 'Ошибка загрузки данных' : 'Error loading data');
      } finally {
        setLoading(false);
      }
    };
    fetchTheses();
  }, [festschrift_section_id, lang]);

  // Отслеживаем наличие несохранённых изменений
  useEffect(() => {
    setUnsavedChanges(JSON.stringify(localRates) !== JSON.stringify(initialRates));
  }, [localRates, initialRates]);

  const handleInputChange = (thesis_id, value) => {
    setLocalRates((prev) => ({
      ...prev,
      [thesis_id]: value,
    }));
  };

  // Навигация к превью отдельной работы
  const handleThesisClick = (thesis_id, thesisName) => {
    attemptNavigation(() => {
      navigate(
        `/publishing/${festschrift_id}/sections/${festschrift_section_id}/election/${thesis_id}` +
          `?conferenceName=${encodeURIComponent(confName)}` +
          `&sectionName=${encodeURIComponent(sectionName)}` +
          `&thesisTitle=${encodeURIComponent(thesisName)}`,
      );
    });
  };

  // Сохранение оценок
  const handleSave = async () => {
    const ratesToSave = [];
    Object.entries(localRates).forEach(([article_id, rateValue]) => {
      if (rateValue !== '' && rateValue !== null) {
        const parsed = parseInt(rateValue, 10);
        if (!isNaN(parsed) && parsed >= -20 && parsed <= 20) {
          ratesToSave.push({
            article_id: parseInt(article_id, 10),
            article_rate: parsed,
          });
        }
      }
    });

    if (ratesToSave.length === 0) {
      alert(lang === 'ru' ? 'Нет оценок для сохранения' : 'No ratings to save');
      return;
    }

    try {
      await api.post(`/rates/section/${festschrift_section_id}/rates`, ratesToSave);
      alert(lang === 'ru' ? 'Оценки успешно сохранены' : 'Ratings saved successfully');
      setInitialRates({ ...localRates });
      setUnsavedChanges(false);

      if (pendingNavigation) {
        pendingNavigation();
        setPendingNavigation(null);
      }
    } catch (err) {
      console.error(err);
      alert(lang === 'ru' ? 'Ошибка сохранения оценок' : 'Error saving ratings');
    }
  };

  const attemptNavigation = (navigateCallback) => {
    if (unsavedChanges) {
      setShowUnsavedModal(true);
      setPendingNavigation(() => navigateCallback);
    } else {
      navigateCallback();
    }
  };

  // Предупреждение при закрытии вкладки/обновлении
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (unsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [unsavedChanges]);

  // Сортировка по названию
  const sortedTheses = useMemo(() => {
    if (sortOrder === 'none') return theses;
    const sorted = [...theses].sort((a, b) => {
      const nameA = getLocalizedName(a.article_title).toLowerCase();
      const nameB = getLocalizedName(b.article_title).toLowerCase();
      if (nameA < nameB) return sortOrder === 'asc' ? -1 : 1;
      if (nameA > nameB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [theses, sortOrder, getLocalizedName]);

  const handleSort = () => {
    if (sortOrder === 'none') setSortOrder('asc');
    else if (sortOrder === 'asc') setSortOrder('desc');
    else setSortOrder('none');
  };

  if (loading) {
    return (
      <div className="rates-loading">
        {lang === 'ru' ? 'Загрузка данных...' : 'Loading data...'}
      </div>
    );
  }

  if (error) {
    return <div className="rates-error">{error}</div>;
  }

  return (
    <div className="editor-scroll-container">
      <div className="rates-container">
        <h1 className="rates-title">{lang === 'ru' ? 'Оценка статей' : 'Article ratings'}</h1>
        <table className="rates-table">
          <thead>
            <tr>
              <th onClick={handleSort}>
                {lang === 'ru' ? 'Статья' : 'Article'}
                {sortOrder === 'asc' ? ' ▲' : sortOrder === 'desc' ? ' ▼' : ''}
              </th>
              <th>{lang === 'ru' ? 'Оценка (-20 до 20)' : 'Rating (-20 to 20)'}</th>
            </tr>
          </thead>
          <tbody>
            {sortedTheses.map((item) => (
              <tr key={item.article_id}>
                <td className="thesis-title">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleThesisClick(item.article_id, getLocalizedName(item.article_title));
                    }}
                  >
                    {getLocalizedName(item.article_title)}
                  </a>
                </td>
                <td>
                  <input
                    type="number"
                    min="-20"
                    max="20"
                    step="1"
                    value={localRates[item.article_id]}
                    onChange={(e) => handleInputChange(item.article_id, e.target.value)}
                    className="rate-input"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="save-btn-container">
          <button className="save-btn" onClick={handleSave}>
            {lang === 'ru' ? 'Сохранить' : 'Save'}
          </button>
        </div>
      </div>

      {showUnsavedModal && (
        <UnsavedChangesModal
          onSave={async () => {
            await handleSave();
            setShowUnsavedModal(false);
          }}
          onLeave={() => {
            setShowUnsavedModal(false);
            if (pendingNavigation) pendingNavigation();
            setPendingNavigation(null);
          }}
        />
      )}
    </div>
  );
};

const UnsavedChangesModal = ({ onSave, onLeave }) => {
  const { getCurrentLanguage } = useLocale();
  const lang = getCurrentLanguage();

  return (
    <div className="modal-overlay">
      <div className="modal-window">
        <p className="modal-message">
          {lang === 'ru'
            ? 'У вас есть несохранённые изменения. Сохранить изменения перед уходом?'
            : 'You have unsaved changes. Save them before leaving?'}
        </p>
        <div className="modal-buttons">
          <button className="modal-confirm-btn" onClick={onSave}>
            {lang === 'ru' ? 'Сохранить' : 'Save'}
          </button>
          <button className="modal-cancel-btn" onClick={onLeave}>
            {lang === 'ru' ? 'Уйти без сохранения' : 'Leave without saving'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThesisRatesEditor;
